import { Component, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { AuthService } from '../auth.service';
import { ThemeService } from './theme.service';

@Component({
  selector: 'app-user-profile-dropdown',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatDividerModule],
  encapsulation: ViewEncapsulation.None,
  template: `
    <button mat-button [matMenuTriggerFor]="userMenu" class="user-profile-button" *ngIf="authService.isLoggedIn()">
      <div class="user-profile-button-container">
        <div class="avatar-container">
          <img 
            *ngIf="authService.userDetails?.profile_image_url" 
            [src]="authService.userDetails?.profile_image_url" 
            [alt]="authService.userDetails?.login + ' avatar'"
            class="user-avatar">
          <mat-icon *ngIf="!authService.userDetails?.profile_image_url">account_circle</mat-icon>
        </div>
        <span class="username">{{ authService.userDetails?.login }}</span>
        <mat-icon>arrow_drop_down</mat-icon>
      </div>
    </button>
    
    <mat-menu #userMenu="matMenu" class="user-profile-menu">
      <button mat-menu-item (click)="toggleDebugStream()">
        <mat-icon>bug_report</mat-icon>
        <span>Debug Mode</span>
      </button>
      <mat-divider></mat-divider>
      <button mat-menu-item (click)="toggleTheme()">
        <mat-icon>{{ (themeService.isDarkMode$ | async) ? 'light_mode' : 'dark_mode' }}</mat-icon>
        <span>{{ (themeService.isDarkMode$ | async) ? 'Light Mode' : 'Dark Mode' }}</span>
      </button>
      <mat-divider></mat-divider>
      <button mat-menu-item (click)="logout()">
        <mat-icon>logout</mat-icon>
        <span>Logout</span>
      </button>
    </mat-menu>
  `,
  styles: [`
    .user-profile-button {
      background-color: var(--surface-color);
      color: var(--text-color);
      border: 1px solid var(--border-color);
      border-radius: 6px;
      padding: 4px 8px;
      display: flex;
      align-items: center;
      gap: 4px;
      min-width: 0;
      transition: background-color 0.3s ease, border-color 0.3s ease;
      
      .username {
        font-size: 14px;
        font-weight: 500;
        max-width: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }

      .user-profile-button-container {
        display: flex;
        flex-direction: row;
        gap: 8px;
      
        .avatar-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 18px !important;
          height: 18px !important;
          overflow: hidden;
          
          .user-avatar {
            width: 18px !important;
            height: 18px !important;
            min-width: 18px !important;
            min-height: 18px !important;
            max-width: 18px !important;
            max-height: 18px !important;
            border-radius: 50%;
            object-fit: cover;
            display: block;
          }
          
          mat-icon {
            font-size: 18px !important;
            width: 18px !important;
            height: 18px !important;
          }
        }
      }
    }
    
    .user-profile-button:hover {
      background-color: var(--hover-background);
      border-color: var(--border-hover);
    }
    
    .user-profile-menu {
      button[mat-menu-item] {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--text-color) !important;
        background-color: var(--menu-background) !important;
        
        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
        }
      }

      button[mat-menu-item]:hover {
        background-color: var(--hover-background) !important;
      }
    }
  `]
})
export class UserProfileDropdownComponent {
  @Output() debugStreamToggle = new EventEmitter<void>();

  constructor(
    public authService: AuthService,
    public themeService: ThemeService
  ) {}

  toggleDebugStream(): void {
    this.debugStreamToggle.emit();
  }

  toggleTheme(): void {
    this.themeService.toggleDarkMode();
  }

  logout(): void {
    this.authService.logout();
  }
}
